// Shared conjugation utilities used by the practice hub and ConjugationPractice

export type ConjTense =
  | 'present' | 'preterite' | 'imperfect'
  | 'future' | 'conditional' | 'present_subjunctive'
  | 'imperfect_subjunctive' | 'present_perfect' | 'pluperfect'

type VerbMeta = { e_present?: string; irregular_forms?: Record<string, string> }

// Module-level cache so data is loaded once per session
let esVerbs: Record<string, VerbMeta> | null = null
let ptVerbs: Record<string, VerbMeta> | null = null
let fiVerbs: Record<string, VerbMeta> | null = null

export async function loadVerbData(langCode: string): Promise<Record<string, VerbMeta>> {
  if (langCode === 'es') {
    if (!esVerbs) {
      const r = await fetch('/api/vocabulary/spanish-verbs.json')
      esVerbs = r.ok ? await r.json() : {}
    }
    return esVerbs!
  }
  if (langCode === 'pt') {
    if (!ptVerbs) {
      const r = await fetch('/api/vocabulary/portuguese-verbs.json')
      ptVerbs = r.ok ? await r.json() : {}
    }
    return ptVerbs!
  }
  if (langCode === 'fi') {
    if (!fiVerbs) {
      const r = await fetch('/api/vocabulary/finnish-verbs.json')
      fiVerbs = r.ok ? await r.json() : {}
    }
    return fiVerbs!
  }
  return {}
}

const TENSE_TITLE_ES: Record<ConjTense, string> = {
  present: 'Presente Indicativo',
  preterite: 'Pretérito Indicativo',
  imperfect: 'Imperfecto Indicativo',
  future: 'Futuro Indicativo',
  conditional: 'Condicional Indicativo',
  present_subjunctive: 'Presente Subjuntivo',
  imperfect_subjunctive: 'Imperfecto Subjuntivo',
  present_perfect: '',
  pluperfect: '',
}
export const TENSE_TITLE_PT: Record<ConjTense, string> = {
  present: 'Presente Indicativo',
  preterite: 'Pretérito Mais-que-perfeito Indicativo',
  imperfect: 'Pretérito Imperfeito Indicativo',
  future: 'Futuro do Presente Indicativo',
  conditional: 'Futuro do Pretérito Condicional',
  present_subjunctive: 'Presente Subjuntivo',
  imperfect_subjunctive: 'Pretérito Imperfeito Subjuntivo',
  present_perfect: '',
  pluperfect: '',
}
export const TENSE_TITLE_FI: Record<ConjTense, string> = {
  present: 'Preesens',
  preterite: 'Imperfekti',
  imperfect: 'Imperfekti',
  future: '',
  conditional: 'Konditionaali',
  present_subjunctive: '',
  imperfect_subjunctive: '',
  present_perfect: 'Perfekti',
  pluperfect: 'Pluskvamperfekti',
}

export const PRONOUNS_FI = ['minä', 'sinä', 'hän', 'me', 'te', 'he']

export const TENSE_DISPLAY: Record<ConjTense, string> = {
  present: 'Present Indicative',
  preterite: 'Preterite',
  imperfect: 'Imperfect',
  future: 'Future',
  conditional: 'Conditional',
  present_subjunctive: 'Present Subjunctive',
  imperfect_subjunctive: 'Imperfect Subjunctive',
  present_perfect: 'Present Perfect',
  pluperfect: 'Pluperfect',
}

function personNumber(pronoun: string): { person: '1a' | '2a' | '3a'; number: 'singular' | 'plural' } {
  switch (pronoun) {
    case 'yo': case 'eu': case 'minä': return { person: '1a', number: 'singular' }
    case 'tú': case 'tu': case 'sinä': return { person: '2a', number: 'singular' }
    case 'él': case 'ella': case 'usted': case 'ele': case 'ela': case 'você': case 'hän':
      return { person: '3a', number: 'singular' }
    case 'nosotros': case 'nós': case 'me': return { person: '1a', number: 'plural' }
    case 'vosotros': case 'vós': case 'te': return { person: '2a', number: 'plural' }
    case 'ellos': case 'ellas': case 'ustedes': case 'eles': case 'elas': case 'vocês': case 'he':
      return { person: '3a', number: 'plural' }
    default: return { person: '3a', number: 'singular' }
  }
}

function lookupIrregular(infinitive: string, pronoun: string, tense: ConjTense, langCode: string, data: Record<string, VerbMeta>): string | null {
  const meta = data[infinitive]
  if (!meta?.irregular_forms) return null
  const { person, number } = personNumber(pronoun)
  const title = langCode === 'pt' ? TENSE_TITLE_PT[tense] : langCode === 'fi' ? TENSE_TITLE_FI[tense] : TENSE_TITLE_ES[tense]
  const unit = langCode === 'pt' ? 'pessoa' : 'persona'
  return meta.irregular_forms[`${title}; ${person} ${unit}; ${number}`] ?? null
}

function getPastParticiple(infinitive: string, langCode: string, data: Record<string, VerbMeta>): string {
  const meta = data[infinitive]
  const ppKey = langCode === 'pt' ? 'Particípio' : 'Participio'
  if (meta?.irregular_forms?.[ppKey]) return meta.irregular_forms[ppKey]
  const group = infinitive.slice(-2)
  if (group === 'ar') return infinitive.slice(0, -2) + 'ado'
  if (group === 'er' || group === 'ir') return infinitive.slice(0, -2) + 'ido'
  return infinitive
}

export function deriveConjugation(infinitive: string, pronoun: string, tense: ConjTense, langCode: string, data: Record<string, VerbMeta>): string {
  const irregular = lookupIrregular(infinitive, pronoun, tense, langCode, data)
  if (irregular) return irregular

  if (tense === 'present_perfect' || tense === 'pluperfect') {
    const pp = getPastParticiple(infinitive, langCode, data)
    if (langCode === 'es') {
      const auxMap = tense === 'present_perfect'
        ? { yo:'he', tú:'has', él:'ha', ella:'ha', usted:'ha', 'él / ella':'ha', nosotros:'hemos', vosotros:'habéis', ellos:'han', ellas:'han', ustedes:'han' }
        : { yo:'había', tú:'habías', él:'había', ella:'había', usted:'había', 'él / ella':'había', nosotros:'habíamos', vosotros:'habíais', ellos:'habían', ellas:'habían', ustedes:'habían' }
      const aux = (auxMap as Record<string, string>)[pronoun]
      return aux ? `${aux} ${pp}` : infinitive
    }
    if (langCode === 'pt') {
      const auxMap = tense === 'present_perfect'
        ? { eu:'tenho', você:'tem', ele:'tem', ela:'tem', 'ele/ela':'tem', nós:'temos', vocês:'têm', eles:'têm', elas:'têm', 'eles/elas':'têm' }
        : { eu:'tinha', você:'tinha', ele:'tinha', ela:'tinha', 'ele/ela':'tinha', nós:'tínhamos', vocês:'tinham', eles:'tinham', elas:'tinham', 'eles/elas':'tinham' }
      const aux = (auxMap as Record<string, string>)[pronoun]
      return aux ? `${aux} ${pp}` : infinitive
    }
  }

  if (langCode === 'pt') return infinitive // fallback; PT needs irregular_forms

  const group = infinitive.slice(-2) as 'ar' | 'er' | 'ir'
  if (!['ar', 'er', 'ir'].includes(group)) return infinitive
  const stem = infinitive.slice(0, -2)

  if (tense === 'present') {
    const e: Record<'ar' | 'er' | 'ir', Record<string, string>> = {
      ar: { yo:'o', tú:'as', él:'a', ella:'a', usted:'a', 'él / ella':'a', nosotros:'amos', vosotros:'áis', ellos:'an', ellas:'an', ustedes:'an' },
      er: { yo:'o', tú:'es', él:'e', ella:'e', usted:'e', 'él / ella':'e', nosotros:'emos', vosotros:'éis', ellos:'en', ellas:'en', ustedes:'en' },
      ir: { yo:'o', tú:'es', él:'e', ella:'e', usted:'e', 'él / ella':'e', nosotros:'imos', vosotros:'ís', ellos:'en', ellas:'en', ustedes:'en' },
    }
    return stem + (e[group][pronoun] ?? '')
  }
  if (tense === 'preterite') {
    const e: Record<'ar' | 'er' | 'ir', Record<string, string>> = {
      ar: { yo:'é', tú:'aste', él:'ó', ella:'ó', usted:'ó', 'él / ella':'ó', nosotros:'amos', vosotros:'asteis', ellos:'aron', ellas:'aron', ustedes:'aron' },
      er: { yo:'í', tú:'iste', él:'ió', ella:'ió', usted:'ió', 'él / ella':'ió', nosotros:'imos', vosotros:'isteis', ellos:'ieron', ellas:'ieron', ustedes:'ieron' },
      ir: { yo:'í', tú:'iste', él:'ió', ella:'ió', usted:'ió', 'él / ella':'ió', nosotros:'imos', vosotros:'isteis', ellos:'ieron', ellas:'ieron', ustedes:'ieron' },
    }
    return stem + (e[group][pronoun] ?? '')
  }
  if (tense === 'imperfect') {
    const e: Record<'ar' | 'er' | 'ir', Record<string, string>> = {
      ar: { yo:'aba', tú:'abas', él:'aba', ella:'aba', usted:'aba', 'él / ella':'aba', nosotros:'ábamos', vosotros:'abais', ellos:'aban', ellas:'aban', ustedes:'aban' },
      er: { yo:'ía', tú:'ías', él:'ía', ella:'ía', usted:'ía', 'él / ella':'ía', nosotros:'íamos', vosotros:'íais', ellos:'ían', ellas:'ían', ustedes:'ían' },
      ir: { yo:'ía', tú:'ías', él:'ía', ella:'ía', usted:'ía', 'él / ella':'ía', nosotros:'íamos', vosotros:'íais', ellos:'ían', ellas:'ían', ustedes:'ían' },
    }
    return stem + (e[group][pronoun] ?? '')
  }
  if (tense === 'future' || tense === 'conditional') {
    const e: Record<ConjTense, Record<string, string>> = {
      future:      { yo:'é', tú:'ás', él:'á', ella:'á', usted:'á', 'él / ella':'á', nosotros:'emos', vosotros:'éis', ellos:'án', ellas:'án', ustedes:'án' },
      conditional: { yo:'ía', tú:'ías', él:'ía', ella:'ía', usted:'ía', 'él / ella':'ía', nosotros:'íamos', vosotros:'íais', ellos:'ían', ellas:'ían', ustedes:'ían' },
    } as any
    return infinitive + (e[tense][pronoun] ?? '')
  }
  if (tense === 'present_subjunctive') {
    const e: Record<'ar' | 'er' | 'ir', Record<string, string>> = {
      ar: { yo:'e', tú:'es', él:'e', ella:'e', usted:'e', 'él / ella':'e', nosotros:'emos', vosotros:'éis', ellos:'en', ellas:'en', ustedes:'en' },
      er: { yo:'a', tú:'as', él:'a', ella:'a', usted:'a', 'él / ella':'a', nosotros:'amos', vosotros:'áis', ellos:'an', ellas:'an', ustedes:'an' },
      ir: { yo:'a', tú:'as', él:'a', ella:'a', usted:'a', 'él / ella':'a', nosotros:'amos', vosotros:'áis', ellos:'an', ellas:'an', ustedes:'an' },
    }
    return stem + (e[group][pronoun] ?? '')
  }
  if (tense === 'imperfect_subjunctive') {
    const e: Record<'ar' | 'er' | 'ir', Record<string, string>> = {
      ar: { yo:'ara', tú:'aras', él:'ara', ella:'ara', usted:'ara', 'él / ella':'ara', nosotros:'áramos', vosotros:'arais', ellos:'aran', ellas:'aran', ustedes:'aran' },
      er: { yo:'iera', tú:'ieras', él:'iera', ella:'iera', usted:'iera', 'él / ella':'iera', nosotros:'iéramos', vosotros:'ierais', ellos:'ieran', ellas:'ieran', ustedes:'ieran' },
      ir: { yo:'iera', tú:'ieras', él:'iera', ella:'iera', usted:'iera', 'él / ella':'iera', nosotros:'iéramos', vosotros:'ierais', ellos:'ieran', ellas:'ieran', ustedes:'ieran' },
    }
    return stem + (e[group][pronoun] ?? '')
  }
  return infinitive
}

export function getVerbType(infinitive: string, data: Record<string, VerbMeta>): string {
  const meta = data[infinitive]
  const hasIrregular = meta?.irregular_forms && Object.keys(meta.irregular_forms).length > 0
  if (hasIrregular) return 'Irregular'
  const group = infinitive.slice(-2)
  if (group === 'ar') return 'Regular -AR'
  if (group === 'er') return 'Regular -ER'
  if (group === 'ir') return 'Regular -IR'
  return 'Regular'
}

const EN_PRONOUN: Record<string, string> = {
  yo:'I', tú:'you', él:'he', ella:'she', usted:'you',
  nosotros:'we', vosotros:'you all', ellos:'they', ellas:'they', ustedes:'you all',
  eu:'I', você:'you', ele:'he', ela:'she', nós:'we', vocês:'you all', eles:'they', elas:'they',
  minä:'I', sinä:'you', hän:'he/she', me:'we', te:'you all', he:'they',
}

export function buildTranslation(pronoun: string, ePresent: string | undefined): string {
  const sub = EN_PRONOUN[pronoun] ?? pronoun
  return ePresent ? `${sub} ${ePresent}` : ''
}

export type DrillCardData = {
  id: number
  verb: string
  tense: ConjTense
  tenseLabel: string
  verbType: string
  pronoun: string
  answer: string
  translation: string
}

export function generateDrillCards(opts: {
  data: Record<string, VerbMeta>
  langCode: string
  pronouns: string[]
  tenses: ConjTense[]
  verbType: 'all' | 'regular' | 'irregular'
  count: number
  priorityVerbs?: string[]
}): DrillCardData[] {
  const { data, langCode, pronouns, tenses, verbType, count, priorityVerbs } = opts
  const allVerbs = Object.keys(data).filter(v => {
    if (langCode === 'fi') return true
    if (v.endsWith('se')) return false
    const g = v.slice(-2)
    return ['ar','er','ir'].includes(g)
  })

  const prioritySet = new Set(priorityVerbs ?? [])

  const pool: DrillCardData[] = []
  // Priority verbs first, then random shuffle of the rest
  const prioritised = allVerbs.filter(v => prioritySet.has(v))
  const rest = allVerbs.filter(v => !prioritySet.has(v)).sort(() => Math.random() - 0.5)
  const shuffled = [...prioritised, ...rest].slice(0, Math.min(200, allVerbs.length))

  for (const inf of shuffled) {
    if (pool.length >= count * 3) break
    const vt = getVerbType(inf, data)
    if (verbType === 'regular' && vt === 'Irregular') continue
    if (verbType === 'irregular' && vt !== 'Irregular') continue
    for (const tense of tenses) {
      for (const pronoun of pronouns) {
        const answer = deriveConjugation(inf, pronoun, tense, langCode, data)
        if (!answer || answer === inf) continue // skip fallbacks
        pool.push({
          id: pool.length,
          verb: inf,
          tense,
          tenseLabel: TENSE_DISPLAY[tense],
          verbType: vt,
          pronoun,
          answer,
          translation: buildTranslation(pronoun, data[inf]?.e_present),
        })
      }
    }
  }

  // Shuffle and trim to requested count
  return pool.sort(() => Math.random() - 0.5).slice(0, count)
}
